import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NotificationService from "../NotificationService";
import {
  subscribeToNotificationCount,
} from "../../utils/notificationPolling";

const EMPLOYEE_UID_CHECK_MS = 2000;
const EMPLOYEE_UID_MAX_ATTEMPTS = 30;

const formatNotificationDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return null;
};

/** Map a notification type to a Font Awesome icon + accent theme class. */
const getNotificationVisual = (type) => {
  switch (type) {
    case "TASK_ASSIGNED":
      return { icon: "fa-user-check", theme: "assigned" };
    case "TASK_UPDATED":
      return { icon: "fa-pen-to-square", theme: "updated" };
    case "TASK_COMPLETED":
      return { icon: "fa-circle-check", theme: "completed" };
    case "TASK_OVERDUE":
      return { icon: "fa-triangle-exclamation", theme: "overdue" };
    case "COMMENT":
      return { icon: "fa-comment-dots", theme: "comment" };
    default:
      return { icon: "fa-bell", theme: "default" };
  }
};

/** Human-readable label for a notification type. */
const getNotificationTypeLabel = (type) => {
  switch (type) {
    case "TASK_ASSIGNED":
      return "Task assigned";
    case "TASK_UPDATED":
      return "Task updated";
    case "TASK_COMPLETED":
      return "Task completed";
    case "TASK_OVERDUE":
      return "Task overdue";
    case "COMMENT":
      return "New comment";
    default:
      return "Notification";
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [userId, setUserId] = useState(() => NotificationService.getUserId());
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // employee_uid may be set asynchronously after login/dashboard load
  useEffect(() => {
    if (userId) return undefined;

    let attempts = 0;
    const syncUserId = () => {
      const id = NotificationService.getUserId();
      if (id) {
        setUserId(id);
        return true;
      }
      return false;
    };

    if (syncUserId()) return undefined;

    const waitId = setInterval(() => {
      attempts += 1;
      if (syncUserId() || attempts >= EMPLOYEE_UID_MAX_ATTEMPTS) {
        clearInterval(waitId);
      }
    }, EMPLOYEE_UID_CHECK_MS);

    return () => clearInterval(waitId);
  }, [userId]);

  // Subscribe to global count updates; skip re-render when value unchanged
  useEffect(() => {
    if (!userId) return undefined;
    return subscribeToNotificationCount((count) => {
      setUnreadCount((prev) => (prev === count ? prev : count));
    });
  }, [userId]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await NotificationService.fetchUserNotifications(userId);
      setNotifications(data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!panelOpen) return undefined;

    fetchNotifications();

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [panelOpen, fetchNotifications]);

  const handleTogglePanel = () => {
    setPanelOpen((prev) => !prev);
  };

  const markAsRead = useCallback(
    async (notification) => {
      if (notification.aa_app_is_read !== 0) return;
      try {
        await NotificationService.markNotificationAsRead(
          notification.aa_app_notification_uid,
          userId
        );
        setNotifications((prev) =>
          prev.map((n) =>
            n.aa_app_notification_uid === notification.aa_app_notification_uid
              ? { ...n, aa_app_is_read: 1 }
              : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    },
    [userId]
  );

  const handleMarkAsRead = async (event, notification) => {
    event.stopPropagation();
    event.preventDefault();
    await markAsRead(notification);
  };

  const handleDeleteNotification = async (event, notification) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      await NotificationService.deleteNotification(
        notification.aa_app_notification_uid,
        userId
      );
      setNotifications((prev) =>
        prev.filter(
          (n) =>
            n.aa_app_notification_uid !== notification.aa_app_notification_uid
        )
      );
      if (notification.aa_app_is_read === 0) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const handleOpenTask = async (notification) => {
    await markAsRead(notification);
    setPanelOpen(false);

    const taskId = notification.aa_app_rl_resource_task_uid;
    if (taskId) {
      navigate(`/update-task/${taskId}`);
    }
  };

  if (!userId) return null;

  return (
    <div className="appbar-notification" ref={containerRef}>
      <button
        type="button"
        className="appbar-notification-trigger"
        onClick={handleTogglePanel}
        aria-expanded={panelOpen}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
        title="Notifications"
      >
        <i className="fas fa-bell" aria-hidden="true" />
        {unreadCount > 0 ? (
          <span className="appbar-notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </button>

      {panelOpen ? (
        <div className="appbar-notification-panel" role="dialog" aria-label="Notifications">
          <div className="appbar-notification-panel-header">
            <div className="appbar-notification-header-title">
              <i className="fas fa-bell" aria-hidden="true" />
              <span>Notifications</span>
            </div>
            {unreadCount > 0 ? (
              <span className="appbar-notification-unread-label">
                {unreadCount} new
              </span>
            ) : null}
          </div>

          <div className="appbar-notification-list">
            {loading ? (
              <div className="appbar-notification-state">
                <span className="appbar-notification-spinner" aria-hidden="true" />
                <span>Loading notifications…</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="appbar-notification-state">
                <i className="far fa-bell-slash appbar-notification-empty-icon" aria-hidden="true" />
                <span>You're all caught up</span>
                <small>No notifications right now</small>
              </div>
            ) : (
              notifications.map((notification) => {
                const isUnread = notification.aa_app_is_read === 0;
                const { icon, theme } = getNotificationVisual(
                  notification.aa_app_notification_type
                );
                const relative = formatRelativeTime(
                  notification.aa_app_task_creation_date
                );
                const typeLabel = getNotificationTypeLabel(
                  notification.aa_app_notification_type
                );
                const taskId = notification.aa_app_rl_resource_task_uid;
                return (
                  <div
                    key={notification.aa_app_notification_uid}
                    className={`appbar-notification-item${isUnread ? " unread" : ""}`}
                  >
                    <div
                      className="appbar-notification-item-main"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenTask(notification)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleOpenTask(notification);
                        }
                      }}
                      title="Open task details"
                    >
                      <span
                        className={`appbar-notification-icon theme-${theme}`}
                        aria-hidden="true"
                      >
                        <i className={`fas ${icon}`} />
                      </span>
                      <span className="appbar-notification-body">
                        <span className="appbar-notification-toprow">
                          <span className={`appbar-notification-tag theme-${theme}`}>
                            {typeLabel}
                          </span>
                          <span
                            className={`appbar-notification-status${
                              isUnread ? " unread" : " read"
                            }`}
                          >
                            <i
                              className={`fas ${
                                isUnread ? "fa-circle" : "fa-check"
                              }`}
                              aria-hidden="true"
                            />
                            {isUnread ? "Unread" : "Read"}
                          </span>
                        </span>
                        <span className="appbar-notification-message">
                          {notification.aa_app_notification_message}
                        </span>
                        <span className="appbar-notification-meta">
                          {taskId ? (
                            <span className="appbar-notification-taskref">
                              <i className="fas fa-hashtag" aria-hidden="true" />
                              Task {taskId}
                            </span>
                          ) : null}
                          <span className="appbar-notification-time">
                            <i className="far fa-clock" aria-hidden="true" />
                            {relative ||
                              formatNotificationDate(
                                notification.aa_app_task_creation_date
                              )}
                          </span>
                        </span>
                      </span>
                    </div>

                    <div className="appbar-notification-actions">
                      {taskId ? (
                        <button
                          type="button"
                          className="appbar-notification-action open"
                          onClick={() => handleOpenTask(notification)}
                          title="Open task details"
                        >
                          <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
                          <span>Open task</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="appbar-notification-action mark"
                        onClick={(event) => handleMarkAsRead(event, notification)}
                        disabled={!isUnread}
                        title={
                          isUnread ? "Mark as read" : "Already read"
                        }
                      >
                        <i
                          className={`fas ${
                            isUnread ? "fa-envelope-open" : "fa-check-double"
                          }`}
                          aria-hidden="true"
                        />
                        <span>{isUnread ? "Mark as read" : "Read"}</span>
                      </button>
                      <button
                        type="button"
                        className="appbar-notification-action delete"
                        onClick={(event) =>
                          handleDeleteNotification(event, notification)
                        }
                        title="Delete notification"
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;
